import { useEffect, useState } from 'react';
import type { Nullable } from 'corgi-types';
import type { GQLLocale } from 'graphql-ts-types';

import { executeQuery } from 'common/gql';
import { useGQLLocale } from 'common/hooks/locale';
import { useLocalization } from 'common/providers/LocaleProvider';
import type { ErrorMessage } from 'common/types/error';
import gqlQuery from '../gql/configurationTrap.gql';
import type { AlternativeArticleVariation, ConfigurationTrapVariables } from '../types';

interface Variables {
  sku: string;
  locale: GQLLocale;
  alternativeVariantKey: string;
  alternativeVariantValue: string;
}

interface Response {
  articles: Array<{ alternativeVariations: AlternativeArticleVariation[] }>;
}

const getErrorMessage = (message: string) => ({
  message,
  translated: true,
  timestamp: Date.now(),
});

const addListIndex = (alternativeVariations: AlternativeArticleVariation[]) => {
  let listIndex = 0;
  const alternatives = alternativeVariations.map(({ groups, ...variant }) => {
    return {
      ...variant,
      groups: groups.map(({ values, ...group }) => ({
        ...group,
        values: values.map(value => ({ ...value, listIndex: listIndex++ })),
      })),
    };
  });

  return {
    listLength: listIndex,
    alternativeVariations: alternatives,
  };
};

const filterInvalidItems = (alternativeVariations: AlternativeArticleVariation[]) =>
  alternativeVariations
    .map(({ groups, ...variant }) => ({
      ...variant,
      groups: groups.filter(group => group.values.length > 0),
    }))
    .filter(({ groups }) => groups.length > 0);

const transformResponse = (alternativeVariations: AlternativeArticleVariation[]) =>
  addListIndex(filterInvalidItems(alternativeVariations));

export const useFetchConfigurationTrap = ({
  sku,
  alternativeVariantKey = '',
  alternativeVariantValue = '',
}: ConfigurationTrapVariables) => {
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState<Nullable<ErrorMessage>>(null);
  const [data, setData] = useState<AlternativeArticleVariation[]>([]);
  const [listLength, setListLength] = useState(0);
  const locale = useGQLLocale();
  const { t } = useLocalization();

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const response = await executeQuery<Response, Variables>({
          variables: {
            sku,
            locale,
            alternativeVariantKey,
            alternativeVariantValue,
          },
          query: gqlQuery,
        });
        const { listLength: length, alternativeVariations } = transformResponse(
          response.articles[0]?.alternativeVariations ?? []
        );
        setData(alternativeVariations);
        setListLength(length);
      } catch {
        setError(getErrorMessage(t('h24_something_went_wrong')));
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  return { isLoading, error, data, listLength };
};
